import { computed, onScopeDispose, ref, toValue, watch } from "vue";
import { normalizeMapBounds } from "#ce/components/SpFinder/engine/map-bounds";
import { parseMapBounds } from "#ce/apps/SpFinderMap/lib/parse-map-bounds";
const STORAGE_KEY = "doctor-finder-map-bounds-scoping-mode";
// Milliseconds a viewport change settles before it counts, by source.
const SETTLE_DELAY = Object.freeze({ drag: 500, zoom: 1000 });
// Milliseconds after a programmatic move during which settled changes are ignored.
const PROGRAMMATIC_PAUSE = 5000;

/**
 * Keeps viewport UI local until auto/manual apply, suppressing programmatic movement and duplicate bounds.
 * `readBounds` returns the map's live bounds; a change and a manual apply use them over the reported
 * ones, which can predate the end of a drag.
 */
export function useFinderMapViewport({
  mode,
  initialAuto = false,
  enabled = true,
  applyBounds,
  setMode,
  storage,
  readBounds,
}) {
  if (!storage) {
    try {
      storage = window.localStorage;
    } catch {
      /* Storage is optional in privacy-restricted embeds. */
    }
  }
  function initialMode() {
    if (["auto", "manual"].includes(toValue(mode))) {
      return toValue(mode) === "auto";
    }
    try {
      const saved = JSON.parse(storage?.getItem(STORAGE_KEY) ?? "null");
      if (["auto", "manual"].includes(saved)) {
        return saved === "auto";
      }
    } catch {
      /* Storage is optional in privacy-restricted embeds. */
    }
    return Boolean(toValue(initialAuto));
  }
  const auto = ref(initialMode());
  const dirty = ref(false);
  let viewport;
  let lastApplied;
  let timer;
  let pauses = 0;
  let generatedZoom;
  const pauseTimers = new Set();
  let disposed = false;
  function cancel() {
    clearTimeout(timer);
    timer = undefined;
  }
  function liveBounds(reported) {
    return normalizeMapBounds(readBounds?.() ?? reported);
  }
  function apply() {
    cancel();
    if (viewport) {
      viewport = liveBounds(viewport) ?? viewport;
    }
    const key = JSON.stringify(viewport);
    if (disposed || !toValue(enabled) || !dirty.value || !viewport || key === lastApplied) {
      return false;
    }
    lastApplied = key;
    dirty.value = false;
    applyBounds({ ...viewport });
    return true;
  }
  function changed({ bounds, source }) {
    if (disposed || !toValue(enabled) || (source === "zoom" && generatedZoom)) {
      return;
    }
    cancel();
    function settle() {
      timer = undefined;
      if (pauses > 0) {
        return;
      }
      viewport = liveBounds(bounds);
      if (!viewport) {
        return;
      }
      dirty.value = JSON.stringify(viewport) !== lastApplied;
      if (auto.value && dirty.value) {
        apply();
      }
    }
    timer = setTimeout(settle, SETTLE_DELAY[source] ?? SETTLE_DELAY.drag);
  }
  // Ignores the zoom a refit generates; drags still count, since a fit never reports one.
  function refitted() {
    clearTimeout(generatedZoom);
    function expire() {
      generatedZoom = undefined;
    }
    generatedZoom = setTimeout(expire, SETTLE_DELAY.zoom);
  }
  function programmatic() {
    pauses += 1;
    function resume() {
      pauseTimers.delete(timer);
      pauses -= 1;
    }
    const timer = setTimeout(resume, PROGRAMMATIC_PAUSE);
    pauseTimers.add(timer);
  }
  function completed(bounds) {
    cancel();
    dirty.value = false;
    const value = parseMapBounds(bounds);
    lastApplied = value ? JSON.stringify(normalizeMapBounds(value)) : undefined;
  }
  function changeMode(value) {
    auto.value = Boolean(value);
    const next = auto.value ? "auto" : "manual";
    try {
      storage?.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* Storage is optional in privacy-restricted embeds. */
    }
    setMode(next);
    if (auto.value) {
      apply();
    } else {
      cancel();
    }
  }
  function receiveMode(value) {
    if (!["auto", "manual"].includes(value)) {
      return;
    }
    auto.value = value === "auto";
    if (auto.value) {
      apply();
    } else {
      cancel();
    }
  }
  watch(() => toValue(mode), receiveMode);
  onScopeDispose(() => {
    disposed = true;
    cancel();
    pauseTimers.forEach(clearTimeout);
    clearTimeout(generatedZoom);
  });
  return {
    auto: computed(() => auto.value),
    dirty: computed(() => dirty.value),
    changed,
    apply,
    changeMode,
    programmatic,
    refitted,
    completed,
  };
}
